import { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import css from './index.module.scss';
import PageLayout from '@/components/PageLayout';
import { useGlobal } from '@/utils/useGlobal';
import ProductNav from '@/components/ProductCommon/ProductNav';
import ProductSupport from '@/components/ProductCommon/ProductSupport';
import Multi3Spec from '@/components/ProductCommon/Multi3Spec';
import SEOlinksA from '@/components/SEOlinksA';
import { getProductFromGeneralKey, getProductMsg } from '@/utils/productGetServerSideProps';
import { productDetailData, productFaqBySpuId } from '@/service';
import PageConPc from '@/components/productRiver/river2';
import PageConH5 from '@/componentsH5/productRiver/river2';
import { useSupportData } from '@/utils/hooks';
import Annotation from '@/components/Annotation';

// 类似river2这种需要展示三种同类产品规格的参数
const formatSpecRiver2Data = (catagory: any, arr: any) => {
    const [p1, p2, p3] = arr;
    const flag1 = p1 && p1.skuList && p1.skuList.length;
    const flag2 = p2 && p2.skuList && p2.skuList.length;
    const flag3 = p3 && p3.skuList && p3.skuList.length;
    if (!flag1 || !flag2 || !flag3) {
        return [];
    }
    const { specGroupList } = catagory[0] || [];
    const skuObjP1 = JSON.parse(typeof p1?.skuList[0]?.ownSpec === 'string' ? p1?.skuList[0]?.ownSpec : JSON.stringify(p1?.skuList[0]?.ownSpec) || '{}');
    const skuObjP2 = JSON.parse(typeof p2?.skuList[0]?.ownSpec === 'string' ? p2?.skuList[0]?.ownSpec : JSON.stringify(p2?.skuList[0]?.ownSpec) || '{}');
    const skuObjP3 = JSON.parse(typeof p3?.skuList[0]?.ownSpec === 'string' ? p3?.skuList[0]?.ownSpec : JSON.stringify(p3?.skuList[0]?.ownSpec) || '{}');
    // 三者共有的secGroups（至少有一个）
    const commonGroups: any = [];
    specGroupList?.map((group: any) => {
        if (skuObjP1[group?.specGroupId] || skuObjP2[group?.specGroupId] || skuObjP3[group?.specGroupId]) {
            commonGroups.push(group);
        }
    });
    // 三者至少有一个specParams
    commonGroups?.map((group: any) => {
        group.newSpecparamList = group?.specParamList?.filter((param: any) => {
            const groupObjP1 = JSON.parse(
                typeof skuObjP1[group?.specGroupId] === 'string' ? skuObjP1[group?.specGroupId] : JSON.stringify(skuObjP1[group?.specGroupId]) || '{}',
            );
            const groupObjP2 = JSON.parse(
                typeof skuObjP2[group?.specGroupId] === 'string' ? skuObjP2[group?.specGroupId] : JSON.stringify(skuObjP2[group?.specGroupId]) || '{}',
            );
            const groupObjP3 = JSON.parse(
                typeof skuObjP3[group?.specGroupId] === 'string' ? skuObjP3[group?.specGroupId] : JSON.stringify(skuObjP3[group?.specGroupId]) || '{}',
            );
            if (groupObjP1[param?.specParamId] || groupObjP2[param?.specParamId] || groupObjP3[param?.specParamId]) {
                return param;
            }
        });
    });
    return commonGroups;
};

// 产品详情页参数
const generalKey = 'product_ps_river_river_256';

export default function Page({ SEO }: any) {
    const { isMobile, gloproductlist } = useGlobal();
    const router = useRouter();
    const { locale = 'us' } = useRouter();
    const PageCon = isMobile ? PageConH5 : PageConPc;
    const theme = 'black';
    const GtmPageLoad = dynamic(() => import('@/components/GtmPageLoad'), { ssr: false });
    // 从全局的列表中--获取产品详情的基本信息
    const productMsg = getProductFromGeneralKey({ gloproductlist, generalKey });

    // 如果不存在该页面,返回404
    // if (!productMsg.generalKey && gloproductlist.length && typeof window !== 'undefined') {
    //     return router.push(`${location.origin}/${router.locale}/404`);
    // }

    // 数据
    const [multi3SpecList, setMulti3SpecList] = useState([]); // 品类规格
    const [productDetailRiver2, setProductDetailRiver2] = useState({}); // river2 详情
    const [productDetailRiver2Max, setProductDetailRiver2Max] = useState({}); // river2max 详情
    const [productDetailRiver2Pro, setProductDetailRiver2Pro] = useState({}); // river2pro 详情

    useEffect(() => {
        (async () => {
            const promiseAll = new Set();
            const products = [productMsg?.url ?? 'river-2-portable-power-station', 'river-2-max-portable-power-station', 'river-2-pro-portable-power-station'];
            products?.map((handle: string) => {
                promiseAll.add(productDetailData({ handle }, locale || 'us'));
            });
            promiseAll.add(productFaqBySpuId({ spuId: productMsg?.sourceId }, locale));
            try {
                const res: any = await Promise.all(promiseAll);
                // river2产品详情
                if (res && res[0] && res[0]?.code === '0') {
                    if (res[0]?.data?.spuResponse) {
                        setProductDetailRiver2(res[0]?.data?.spuResponse);
                    }
                }
                // river2max产品详情
                if (res && res[1] && res[1]?.code === '0') {
                    if (res[1]?.data?.spuResponse) {
                        setProductDetailRiver2Max(res[1]?.data?.spuResponse);
                    }
                }
                // river2pro产品详情
                if (res && res[2] && res[2]?.code === '0') {
                    if (res[2]?.data?.spuResponse) {
                        setProductDetailRiver2Pro(res[2]?.data?.spuResponse);
                    }
                }
                const flag = res && res[0] && res[0]?.code === '0' && res && res[1] && res[1]?.code === '0' && res && res[2] && res[2]?.code === '0';
                if (flag) {
                    const catagory = res[0]?.data?.productCatagoryResponseList;
                    const river2 = res[0]?.data?.spuResponse;
                    const river2max = res[1]?.data?.spuResponse;
                    const river2pro = res[2]?.data?.spuResponse;
                    setMulti3SpecList(formatSpecRiver2Data(catagory, [river2, river2max, river2pro]));
                }
            } catch (e: any) {
                console.error('error', e);
            }
        })();
    }, []);

    const [_, $, supportData] = useSupportData(getProductMsg, locale, productMsg);
    // 切换tab
    const tabState = (val: any) => {
        document.querySelector('.product-tab.show-tab')?.classList.remove('show-tab');
        document.querySelector(`.product-tab-${val}`)?.classList.add('show-tab');
    };

    const seoTitle = useMemo(() => {
        if (locale === 'za') return 'EcoFlow RIVER 2 Pro';
        // 除 za 外其他优先用接口数据，没有则用默认数据
        if (productMsg.title) return productMsg.title;
        if (locale === 'ph') return 'EcoFlow RIVER 2 – Compact Power';
        return productMsg.title;
    }, [locale, productMsg.title]);

    const seoDescription = useMemo(() => {
        if (locale === 'za')
            return 'Stay powered anywhere with EcoFlow RIVER 2 Pro: the best portable power station within Stage 6 load shedding. 768Wh capacity, 1600W X-Boost. Get yours now!';
        if (productMsg.description) return productMsg.description;
        if (locale === 'ph')
            return 'Chase for lightweight, fast-charging backup power ideal for travel, outdoor use, or emergency needs in the Philippines. Order yours River 2 now.';
        return productMsg.description;
    }, [locale, productMsg.description]);

    return (
        <>
            <SEO title={seoTitle} description={seoDescription} />
            <SEOlinksA
                list={['us', 'za', 'jp', 'cn', 'ca', 'uk', 'de', 'fr', 'it', 'es', 'cis', 'sa', 'au', 'br', 'mx', 'ph']}
                locale={locale}
                pathname={productMsg.url}
            />
            <GtmPageLoad />
            <PageLayout className={`${css.productcontent} ${theme} product_common product_common_real`} theme={theme}>
                <ProductNav
                    className="wbg"
                    title={productMsg?.name}
                    theme={theme}
                    buyLink={locale === 'tw' ? '' : productMsg?.buyLink || ''}
                    buyText={locale === 'de' ? 'Kaufen' : undefined}
                    callback={tabState}
                    productKey={productDetailRiver2?.generalKey}
                />
                <div className="product-tab product-tab-Overview show-tab">
                    <PageCon />
                    <Annotation borderTopColor="transparent" bgColor="transparent" boxStyle={isMobile ? {} : { width: '1120px' }} />
                </div>
                <div className="product-tab product-tab-Specs">
                    <Multi3Spec
                        productCatagoryData={multi3SpecList}
                        theme={theme}
                        p1={productDetailRiver2}
                        p2={productDetailRiver2Max}
                        p3={productDetailRiver2Pro}
                    />
                </div>
                <div className="product-tab product-tab-Support">
                    <ProductSupport theme={theme} supportData={supportData}></ProductSupport>
                </div>
            </PageLayout>
        </>
    );
}
